import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Plus, 
  Settings,
  UserPlus,
  Mail,
  ChevronRight,
  Crown,
  Shield,
  Target,
  TrendingUp,
  Bell
} from 'lucide-react';
import { teamsApi, invitesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const Teams = () => {
  const [teams, setTeams] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [teamsRes, invitesRes] = await Promise.all([
        teamsApi.getAll(),
        invitesApi.getPending().catch(() => ({ data: [] })),
      ]);
      setTeams(teamsRes.data || []);
      setPendingInvites(invitesRes.data || []);
    } catch (error) {
      console.error('Failed to load teams:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      const response = await teamsApi.create(formData);
      setTeams(prev => [...prev, response.data]);
      setShowCreateForm(false);
      setFormData({ name: '', description: '' });
      toast.success('Team created!');
    } catch (error) {
      toast.error('Failed to create team');
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      await invitesApi.accept(inviteId);
      toast.success('Joined the team!');
      loadData();
    } catch (error) {
      toast.error('Failed to accept invite');
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      await invitesApi.decline(inviteId);
      setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
      toast.success('Invite declined');
    } catch (error) {
      toast.error('Failed to decline invite');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="teams-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
            Teams
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Collaborate with your team on shared strategic goals.
          </p>
        </div>
        <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full px-6 gap-2" data-testid="create-team-btn">
              <Plus className="w-4 h-4" />
              Create Team
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
            <DialogHeader>
              <DialogTitle className="text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>Create New Team</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTeam} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label className="text-slate-600 dark:text-slate-300">Team Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Marketing Team"
                  required
                  className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500"
                  data-testid="team-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600 dark:text-slate-300">Description (optional)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What is this team working on?"
                  className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500"
                  data-testid="team-description-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCreateForm(false)}
                  className="rounded-full border-slate-200 dark:border-[#1F2937] text-slate-600 dark:text-slate-300 hover:bg-[#1F2937]"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full"
                  data-testid="create-team-submit-btn"
                >
                  Create Team
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-6" data-testid="pending-invites">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-amber-400" />
            <span className="font-semibold text-amber-300">Pending Invitations</span>
            <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-medium">
              {pendingInvites.length}
            </span>
          </div>
          <div className="space-y-3">
            {pendingInvites.map((invite, index) => (
              <div key={invite.id} className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-xl p-4 flex items-center justify-between" data-testid={`invite-${index}`}>
                <div>
                  <h4 className="font-medium text-slate-800 dark:text-slate-100">{invite.team_name}</h4>
                  {invite.team_description && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{invite.team_description}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeclineInvite(invite.id)}
                    className="rounded-full border-slate-200 dark:border-[#1F2937] text-slate-500 dark:text-slate-400 hover:bg-[#1F2937]"
                    data-testid={`decline-invite-${index}-btn`}
                  >
                    Decline
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleAcceptInvite(invite.id)}
                    className="bg-[#26C6DA] hover:bg-[#0891B2] text-black rounded-full"
                    data-testid={`accept-invite-${index}-btn`}
                  >
                    Accept
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Teams Grid */}
      {teams.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teams.map((team, index) => (
            <div 
              key={team.id}
              onClick={() => navigate(`/teams/${team.id}`)}
              className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6 cursor-pointer hover:border-[#26C6DA]/50 hover:-translate-y-1 transition-all duration-300"
              data-testid={`team-card-${index}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] flex items-center justify-center text-white font-bold text-lg">
                  {team.name.charAt(0).toUpperCase()}
                </div>
                <ChevronRight className="w-5 h-5 text-slate-500" />
              </div>
              
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
                {team.name}
              </h3>
              {team.description && (
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-4 line-clamp-2">{team.description}</p>
              )}
              
              <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-[#1F2937]">
                <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400 text-sm">
                  <Users className="w-4 h-4" />
                  <span>{team.members?.length || 0} members</span>
                </div>
                <div className="flex -space-x-2">
                  {team.members?.slice(0, 4).map((member, i) => (
                    <div 
                      key={member.user_id}
                      className="w-7 h-7 rounded-full bg-[#26C6DA] border-2 border-[#111827] flex items-center justify-center text-black text-xs font-medium"
                      title={member.name}
                    >
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                  ))}
                  {(team.members?.length || 0) > 4 && (
                    <div className="w-7 h-7 rounded-full bg-[#1F2937] border-2 border-[#111827] flex items-center justify-center text-slate-600 dark:text-slate-300 text-xs font-medium">
                      +{team.members.length - 4}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-12 text-center">
          <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
            No Teams Yet
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
            Create a team to collaborate with others on shared strategic goals.
          </p>
          <Button 
            onClick={() => setShowCreateForm(true)}
            className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full px-6 gap-2"
            data-testid="empty-create-team-btn"
          >
            <Plus className="w-4 h-4" />
            Create Your First Team
          </Button>
        </div>
      )}
    </div>
  );
};

export default Teams;
